import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import OrderDetailsComponent from '../../components/OrderDetails';
import OrderDetailsSkeletonComponent from '../../components/OrderDetailsSkeleton';

import {
  resetInteraction,
  requestInteraction,
  setOrderHelpModalIsOpen,
  setOrderHelpModalPage,
  setIsSpecificHelpPage,
} from '../../actions/orderActions';

import { isFeatureActive, whichVariant } from '../../selectors/configSelectors';
import { getCurrentQuery } from '../../selectors/requestSelectors';
import { getOrderPunchCard } from '../../selectors/orderPunchCardsSelectors';
import { getOrderStatus, getShouldShowInfoBanner } from '../../selectors/orderStatusSelectors';
import { selectOrderConfirmationReference } from '../../selectors/orderConfirmationReference';
import { selectDirectionHelpAction } from '../../selectors/directionHelpAction';
import { CX_OH_DYNAMIC_ACTIONS_CALL_RIDER } from '../../flags/features';
import { MVT_POE_12 } from '../../flags/experiments';

import getSafe from '../../utils/getSafe';

export const OrderDetailsColumn = ({
  order,
  contactDetails,
  riderName,
  riderSubtitle,
  isSharedOrder,
  shouldShowInfoBanner,
  shouldShowPunchCard,
  orderConfirmationReference,
  directionHelpAction,
  orderHelpDynamicActionsCallRiderWebOn,
  isPolling,
  openOrderHelpModal,
  openOrderHelpModalToCallRiderPage,
}) => {
  if (order) {
    return (
      <OrderDetailsComponent
        id={order.id}
        deliveryAddress={order.deliveryAddress}
        deliveryNote={order.deliveryNote}
        orderDetails={order.orderDetails}
        restaurantName={order.restaurantName}
        totalAmount={order.totalAmount}
        imageUrl={order.imageUrl || ''}
        totals={order.totals}
        placedAtMessage={order.placedAtMessage}
        riderName={riderName}
        riderSubtitle={riderSubtitle}
        contactDetails={contactDetails}
        isSharedOrder={isSharedOrder}
        shouldShowInfoBanner={shouldShowInfoBanner}
        shouldShowPunchCard={shouldShowPunchCard}
        orderHelpDynamicActionsCallRiderWebOn={orderHelpDynamicActionsCallRiderWebOn}
        openOrderHelpModal={openOrderHelpModal}
        openOrderHelpModalToCallRiderPage={openOrderHelpModalToCallRiderPage}
        orderConfirmationReference={orderConfirmationReference}
        directionHelpAction={directionHelpAction}
      />
    );
  }

  return (
    <OrderDetailsSkeletonComponent isPolling={isPolling} openOrderHelpModal={openOrderHelpModal} />
  );
};

OrderDetailsColumn.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    deliveryAddress: PropTypes.string,
    deliveryNote: PropTypes.string,
    orderDetails: PropTypes.object.isRequired,
    restaurantName: PropTypes.string.isRequired,
    totalAmount: PropTypes.string,
    imageUrl: PropTypes.string.isRequired,
    totals: PropTypes.arrayOf(PropTypes.object),
    placedAtMessage: PropTypes.string,
  }),
  contactDetails: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  riderName: PropTypes.string,
  riderSubtitle: PropTypes.string,
  isSharedOrder: PropTypes.bool.isRequired,
  orderHelpDynamicActionsCallRiderWebOn: PropTypes.bool,
  isPolling: PropTypes.bool,
  /**
   * Action to open the OrderHelpModal
   */
  openOrderHelpModal: PropTypes.func,
  /**
   * Opens the Order Help straight to the call rider page
   */
  openOrderHelpModalToCallRiderPage: PropTypes.func,
  /**
   * Whether the user is in the punchCard experiment
   */
  shouldShowInfoBanner: PropTypes.bool,
  /**
   * Whether to show the punch card
   */
  shouldShowPunchCard: PropTypes.bool,
  /**
   * Details for the order confirmation reference which is a modal with the order ID
   * to show to collect the order
   */
  orderConfirmationReference: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Details for the direction help which is a modal with links for directions to the restaurant
   * to go to collect the order
   */
  directionHelpAction: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

OrderDetailsColumn.defaultProps = {
  order: null,
  riderName: null,
  riderSubtitle: null,
  contactDetails: null,
  isPolling: true,
  orderHelpDynamicActionsCallRiderWebOn: false,
  shouldShowInfoBanner: true,
  shouldShowPunchCard: false,
  orderConfirmationReference: null,
  directionHelpAction: null,
  openOrderHelpModal: () => {},
  openOrderHelpModalToCallRiderPage: () => {},
};

const mapStateToProps = (state) => {
  const { order, rider, contactDetails, isSharedOrder } = getOrderStatus(state);
  const orderConfirmationReference = selectOrderConfirmationReference(state);
  const directionHelpAction = selectDirectionHelpAction(state);
  const { orderId } = getCurrentQuery(state);
  const punchCardsState = getOrderPunchCard(orderId)(state);
  const isPunchCardVariant = whichVariant(state, MVT_POE_12) === 'punchcard';

  return {
    order,
    contactDetails,
    riderName: rider && rider.name,
    riderSubtitle: rider && rider.subtitle,
    isSharedOrder: isSharedOrder || false,
    orderHelpDynamicActionsCallRiderWebOn: isFeatureActive(state, CX_OH_DYNAMIC_ACTIONS_CALL_RIDER),
    shouldShowInfoBanner: getShouldShowInfoBanner(state),
    shouldShowPunchCard: isPunchCardVariant && getSafe(() => punchCardsState.shouldShow),
    orderConfirmationReference,
    directionHelpAction,
  };
};

const mapActionsToProps = (dispatch) => ({
  openOrderHelpModal: () => {
    dispatch(resetInteraction());
    dispatch(setIsSpecificHelpPage(false));
    dispatch(
      setOrderHelpModalPage({
        actionId: null,
        actionTemplate: null,
        selectedActionId: null,
        selectedActionTemplate: null,
      }),
    );
    dispatch(requestInteraction());
    dispatch(setOrderHelpModalIsOpen(true));
  },
  openOrderHelpModalToCallRiderPage: (canContactActionId) => {
    dispatch(resetInteraction());
    dispatch(setIsSpecificHelpPage(true));
    dispatch(
      setOrderHelpModalPage({
        actionId: 'order_help',
        actionTemplate: 'action_to_action',
        selectedActionId: canContactActionId,
        selectedActionTemplate: 'call_number',
      }),
    );
    dispatch(requestInteraction());
    dispatch(setOrderHelpModalIsOpen(true));
  },
});

export default connect(
  mapStateToProps,
  mapActionsToProps,
)(OrderDetailsColumn);
